import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { PROVIDERS, modelWarnings, visibleModels } from './aiModels';

const providerLabel = (value) => (PROVIDERS.find(p => p.value === value) || { label: value }).label;

// The label is edited in place and saved when the field loses focus or
// Enter is pressed; Escape puts the saved label back.
function LabelField({ model, disabled, onSave }) {
    const [draft, setDraft] = useState(model.label);
    const save = () => {
        const label = draft.trim();
        if (!label || label === model.label) { setDraft(model.label); return; }
        onSave(label);
    };
    return (
        <input
            className="st-input st-label-input"
            aria-label={`Label for ${model.model_id}`}
            value={draft}
            disabled={disabled}
            onChange={e => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={e => {
                if (e.key === 'Enter') e.target.blur();
                if (e.key === 'Escape') setDraft(model.label);
            }}
        />
    );
}

// Every model the CMS knows about. Enabled models are offered in the task
// dropdowns; the rest stay out of the way unless "Show disabled" is on.
export default function AiCatalogTable({ models, busy, onUpdate }) {
    const [query, setQuery] = useState('');
    const [showDisabled, setShowDisabled] = useState(false);
    const rows = visibleModels(models, { query, showDisabled });

    return (
        <div>
            <div className="st-toolbar">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <input
                        className="st-input"
                        type="search"
                        placeholder="Search models"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                    />
                </span>
                <label className="st-check">
                    <input type="checkbox" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
                    Show disabled
                </label>
                <span className="st-count">{rows.length} of {models.length} models</span>
            </div>
            <table className="st-table st-catalog-table">
                <thead>
                    <tr>
                        <th>Enabled</th>
                        <th>Label</th>
                        <th>Model id</th>
                        <th>Provider</th>
                        <th>Notes</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr><td colSpan={5} className="st-empty">No models match.</td></tr>
                    )}
                    {rows.map(m => {
                        const rowBusy = busy === `model-${m.id}`;
                        return (
                            <tr key={m.id} className={m.enabled ? '' : 'st-row-disabled'}>
                                <td>
                                    <input
                                        type="checkbox"
                                        aria-label={`Enable ${m.model_id}`}
                                        checked={m.enabled}
                                        disabled={rowBusy}
                                        onChange={e => onUpdate(m, { enabled: e.target.checked })}
                                    />
                                </td>
                                <td><LabelField key={m.label} model={m} disabled={rowBusy} onSave={label => onUpdate(m, { label })} /></td>
                                <td className="st-mono">{m.model_id}</td>
                                <td>{providerLabel(m.provider)}</td>
                                <td className="st-notes">
                                    {m.assigned_tasks.length > 0 && (
                                        <span className="st-badge st-badge-assigned">Assigned: {m.assigned_tasks.length} task{m.assigned_tasks.length === 1 ? '' : 's'}</span>
                                    )}
                                    {m.source === 'manual' && <span className="st-badge">Added by hand</span>}
                                    {modelWarnings(m).map(w => <span key={w} className="st-badge st-badge-warning">{w}</span>)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

AiCatalogTable.propTypes = {
    models: PropTypes.array.isRequired,
    busy: PropTypes.string,
    onUpdate: PropTypes.func.isRequired,
};
